import {
  createContext,
  useContext,
  useId,
  useMemo,
  useState,
  type FormEvent,
  type JSX,
  type ReactNode,
} from 'react';
import { useIntl } from 'react-intl';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@caratuva/ui';
import { CheckCircle2 } from 'lucide-react';
import { submitContactLead } from '../lib/api.ts';

// Sales-contact modal, exposed app-wide via context so any "Talk to sales" CTA
// (hero, pricing, final band) opens the same dialog. The form posts to the API,
// which emails the lead to the sales inbox.

type SalesContactContextValue = { open: () => void };

const SalesContactContext = createContext<SalesContactContextValue | null>(null);

export function useSalesContact(): SalesContactContextValue {
  const ctx = useContext(SalesContactContext);
  if (!ctx) {
    throw new Error('useSalesContact must be used within <SalesContactProvider>');
  }
  return ctx;
}

export function SalesContactProvider({ children }: { children: ReactNode }): JSX.Element {
  const [open, setOpen] = useState(false);
  const value = useMemo<SalesContactContextValue>(() => ({ open: () => setOpen(true) }), []);
  return (
    <SalesContactContext.Provider value={value}>
      {children}
      <SalesContactDialog open={open} onOpenChange={setOpen} />
    </SalesContactContext.Provider>
  );
}

type Status = 'idle' | 'submitting' | 'success' | 'error';

const EMPTY_FORM = {
  companyName: '',
  contactName: '',
  email: '',
  phone: '',
  message: '',
  website: '', // honeypot
};

function SalesContactDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const intl = useIntl();
  const fieldId = useId();
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });

  function handleOpenChange(next: boolean): void {
    onOpenChange(next);
    if (!next) {
      // Reset after the close animation so the form doesn't visibly empty out.
      window.setTimeout(() => {
        setForm({ ...EMPTY_FORM });
        setStatus('idle');
        setError(null);
      }, 200);
    }
  }

  function update(key: keyof typeof form, val: string): void {
    setForm((f) => ({ ...f, [key]: val }));
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (status === 'submitting') return;
    setStatus('submitting');
    setError(null);
    try {
      await submitContactLead({
        companyName: form.companyName.trim(),
        contactName: form.contactName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || undefined,
        message: form.message.trim() || undefined,
        website: form.website || undefined,
      });
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  const submitting = status === 'submitting';

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="text-[var(--color-navy-900)]">
        {status === 'success' ? (
          <div className="flex flex-col items-center gap-3 py-4 text-center">
            <CheckCircle2 className="h-10 w-10 text-emerald-600" strokeWidth={1.5} />
            <DialogHeader className="items-center">
              <DialogTitle>{intl.formatMessage({ id: 'sales.success.title' })}</DialogTitle>
              <DialogDescription>
                {intl.formatMessage({ id: 'sales.success.body' })}
              </DialogDescription>
            </DialogHeader>
            <Button type="button" className="mt-2" onClick={() => handleOpenChange(false)}>
              {intl.formatMessage({ id: 'sales.success.close' })}
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>{intl.formatMessage({ id: 'sales.title' })}</DialogTitle>
              <DialogDescription>{intl.formatMessage({ id: 'sales.subtitle' })}</DialogDescription>
            </DialogHeader>
            <form className="grid gap-4" onSubmit={(e) => void onSubmit(e)} noValidate>
              <div className="grid gap-1.5">
                <Label htmlFor={`${fieldId}-company`}>
                  {intl.formatMessage({ id: 'sales.form.company' })}
                </Label>
                <Input
                  id={`${fieldId}-company`}
                  required
                  autoComplete="organization"
                  value={form.companyName}
                  onChange={(e) => update('companyName', e.target.value)}
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor={`${fieldId}-name`}>
                  {intl.formatMessage({ id: 'sales.form.name' })}
                </Label>
                <Input
                  id={`${fieldId}-name`}
                  required
                  autoComplete="name"
                  value={form.contactName}
                  onChange={(e) => update('contactName', e.target.value)}
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor={`${fieldId}-email`}>
                  {intl.formatMessage({ id: 'sales.form.email' })}
                </Label>
                <Input
                  id={`${fieldId}-email`}
                  type="email"
                  required
                  autoComplete="email"
                  value={form.email}
                  onChange={(e) => update('email', e.target.value)}
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor={`${fieldId}-phone`}>
                  {intl.formatMessage({ id: 'sales.form.phone' })}
                </Label>
                <Input
                  id={`${fieldId}-phone`}
                  type="tel"
                  autoComplete="tel"
                  value={form.phone}
                  onChange={(e) => update('phone', e.target.value)}
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor={`${fieldId}-message`}>
                  {intl.formatMessage({ id: 'sales.form.message' })}
                </Label>
                <textarea
                  id={`${fieldId}-message`}
                  rows={3}
                  className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  value={form.message}
                  onChange={(e) => update('message', e.target.value)}
                />
              </div>

              {/* Honeypot: off-screen + aria-hidden. Real users never fill it;
                  naive bots do, and the API silently drops those. */}
              <div
                aria-hidden
                className="pointer-events-none absolute left-[-9999px] top-[-9999px] h-0 w-0 overflow-hidden"
              >
                <label htmlFor={`${fieldId}-website`}>Website</label>
                <input
                  id={`${fieldId}-website`}
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.website}
                  onChange={(e) => update('website', e.target.value)}
                />
              </div>

              {status === 'error' && error ? (
                <p className="text-sm text-red-600" role="alert">
                  {error}
                </p>
              ) : null}

              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting
                  ? intl.formatMessage({ id: 'sales.form.submitting' })
                  : intl.formatMessage({ id: 'sales.form.submit' })}
              </Button>
              <p className="text-center text-xs text-[var(--color-navy-500)]">
                {intl.formatMessage({ id: 'sales.form.privacy' })}
              </p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
